import { APIResponse, PlanResponse, ProjectStatus, GameDescriptionForm, Asset } from '../types';

// 各服务默认地址（可通过环境变量覆盖），与 docs/API-接口文档.md 对齐
const DEFAULT_ENDPOINTS = {
  ASSET_PLANNER: 'https://asset-planner.sgqjpw2023.workers.dev',
  STATUS_API: 'https://status-api.sgqjpw2023.workers.dev',
  QUEUE_PRODUCER: 'https://queue-producer.sgqjpw2023.workers.dev',
};

const ENDPOINTS = {
  ASSET_PLANNER: (import.meta as any).env?.VITE_ASSET_PLANNER_URL || DEFAULT_ENDPOINTS.ASSET_PLANNER,
  STATUS_API: (import.meta as any).env?.VITE_STATUS_API_URL || DEFAULT_ENDPOINTS.STATUS_API,
  QUEUE_PRODUCER: (import.meta as any).env?.VITE_QUEUE_PRODUCER_URL || DEFAULT_ENDPOINTS.QUEUE_PRODUCER,
};

// 全局请求超时（可通过 VITE_API_TIMEOUT_MS 覆盖），默认 45 秒
const REQUEST_TIMEOUT_MS = Number((import.meta as any).env?.VITE_API_TIMEOUT_MS) || 45000;

// 真实联调模式：移除所有演示模式/Mock 降级

class ApiService {
  private async makeRequest<T>(
    url: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    const controller = new AbortController();
    const startedAt = Date.now();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      signal: controller.signal,
      ...options,
    };

    try {
      console.log('[api] fetch ->', { url, method: defaultOptions.method || 'GET', timeoutMs: REQUEST_TIMEOUT_MS });
      const response = await fetch(url, defaultOptions);
      clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      const elapsed = Date.now() - startedAt;
      console.warn('[api] request failed:', { url, elapsedMs: elapsed, error });
      throw error;
    }
  }

  // 创建素材规划
  async createPlan(formData: GameDescriptionForm): Promise<PlanResponse> {
    try {
      const url = `${ENDPOINTS.ASSET_PLANNER}/plan`;
      console.log('[api] POST', url);
      const response = await this.makeRequest<PlanResponse>(url, {
        method: 'POST',
        body: JSON.stringify(formData),
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || '素材规划创建失败');
      }

      const plan = response.data;
      console.log('[api] plan ok projectId=', plan.projectId, 'assets=', plan.assets?.length);

      // 初始化项目到状态服务（让状态服务有与队列一致的资产ID）
      try {
        const initUrl = `${ENDPOINTS.STATUS_API}/init-project`;
        console.log('[api] init-project ->', initUrl);
        await this.makeRequest(initUrl, {
          method: 'POST',
          body: JSON.stringify({ projectId: plan.projectId, assets: plan.assets }),
        });
        console.log('[api] init-project ok');
      } catch (e) {
        console.warn('[api] init-project error:', e);
      }

      // 将生成的素材投递到队列，触发图片生成（如果队列未配置则忽略）
      try {
        console.log('[api] send-assets ->', `${ENDPOINTS.QUEUE_PRODUCER}/send-assets`);
        await this.sendAssetsToQueue(plan.projectId, plan.assets);
        console.log('[api] send-assets ok');
      } catch (e) {
        console.warn('[api] send-assets error:', e);
      }

      return plan;
    } catch (error) {
      console.error('[api] createPlan error:', error);
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  // 查询项目状态
  async getProjectStatus(projectId: string): Promise<ProjectStatus> {
    const url = `${ENDPOINTS.STATUS_API}/status?projectId=${projectId}`;
    console.log('[api] GET', url);
    const response = await this.makeRequest<ProjectStatus>(url);

    if (!response.success || !response.data) {
      throw new Error(response.error || '项目状态查询失败');
    }

    return response.data;
  }

  // 更新素材状态（开发/测试用）
  async updateAssetStatus(
    projectId: string,
    assetId: string,
    status: string,
    imageUrl?: string
  ): Promise<void> {
    const url = `${ENDPOINTS.STATUS_API}/update-asset`;
    console.log('[api] POST', url, { projectId, assetId, status, imageUrl });
    const response = await this.makeRequest(url, {
      method: 'POST',
      body: JSON.stringify({
        projectId,
        assetId,
        status,
        imageUrl,
      }),
    });

    if (!response.success) {
      throw new Error(response.error || '素材状态更新失败');
    }
  }

  // 重置项目（开发/测试用）
  async resetProject(projectId: string): Promise<void> {
    const url = `${ENDPOINTS.STATUS_API}/reset?projectId=${projectId}`;
    console.log('[api] POST', url);
    const response = await this.makeRequest(url, {
      method: 'POST',
    });

    if (!response.success) {
      throw new Error(response.error || '项目重置失败');
    }
  }

  // 发送素材到队列（触发生成）
  private async sendAssetsToQueue(projectId: string, assets: Asset[]): Promise<void> {
    const res = await this.makeRequest<{ sentTasks: number }>(`${ENDPOINTS.QUEUE_PRODUCER}/send-assets`, {
      method: 'POST',
      body: JSON.stringify({ projectId, assets }),
    });
    if (!res.success) {
      // 队列未配置等情况：不中断主流程
      const err = res.error || '发送素材到队列失败';
      throw new Error(err);
    }
  }

  // 创建SSE连接
  createSSEConnection(
    projectId: string,
    onMessage: (data: ProjectStatus) => void,
    onError: (error: Error) => void
  ): EventSource | { close: () => void } {
    const url = `${ENDPOINTS.STATUS_API}/status/sse?projectId=${projectId}`;
    console.log('[api] SSE connect ->', url);

    const eventSource = new EventSource(url);
    let hasConnectionError = false;
    let isCompleted = false;

    // 连接超时：仅告警，不降级
    const connectionTimeout = setTimeout(() => {
      if (hasConnectionError) return;
      console.warn('[api] SSE connect timeout');
      onError(new Error('SSE连接超时'));
    }, 5000);

    eventSource.onopen = (event) => {
      console.log('[api] SSE open <-', event);
      clearTimeout(connectionTimeout);

      // 设置消息超时 - 如果10秒内没有收到消息，主动查询状态
      const messageTimeout = setTimeout(() => {
        if (hasConnectionError) return;
        console.warn('[api] SSE no message within 10s, poll /status');
        this.getProjectStatus(projectId)
          .then((status) => {
            console.log('[api] poll /status ok');
            onMessage(status);
          })
          .catch((err) => {
            console.error('[api] poll /status failed:', err);
            onError(new Error('SSE无消息且查询失败'));
          });
      }, 10000);

      // 保存timeout引用以便后续清理
      (eventSource as any).messageTimeout = messageTimeout;
    };

    // 监听命名事件（服务器端 event: status / completed）
    const handleJson = (raw: string, kind: 'status' | 'completed' | 'message') => {
      try {
        const data = JSON.parse(raw);
        if (kind === 'completed' && (data as any).finalStatus) {
          onMessage((data as any).finalStatus as ProjectStatus);
          // 收到完成事件后，主动关闭并阻止后续错误弹窗/重连
          isCompleted = true;
          try { eventSource.close(); } catch {}
          console.log('[api] SSE completed -> closed by client');
        } else {
          onMessage(data as ProjectStatus);
        }
      } catch (e) {
        console.error('[api] SSE parse error:', e, 'raw=', raw?.slice?.(0, 200));
        onError(new Error('SSE数据解析失败'));
      }
    };

    eventSource.addEventListener('status', (ev: MessageEvent) => {
      console.debug('[api] SSE event: status len=', ev.data?.length);
      clearTimeout(connectionTimeout);
      if ((eventSource as any).messageTimeout) clearTimeout((eventSource as any).messageTimeout);
      handleJson(ev.data, 'status');
    });

    eventSource.addEventListener('completed', (ev: MessageEvent) => {
      console.debug('[api] SSE event: completed len=', ev.data?.length);
      clearTimeout(connectionTimeout);
      if ((eventSource as any).messageTimeout) clearTimeout((eventSource as any).messageTimeout);
      handleJson(ev.data, 'completed');
    });

    // 兼容无事件名的消息（如果后端未来做了变更）
    eventSource.onmessage = (event) => {
      console.debug('[api] SSE message len=', event.data?.length);
      clearTimeout(connectionTimeout);
      if ((eventSource as any).messageTimeout) clearTimeout((eventSource as any).messageTimeout);
      handleJson(event.data, 'message');
    };

    eventSource.onerror = (event) => {
      clearTimeout(connectionTimeout);

      // 清除消息超时定时器
      if ((eventSource as any).messageTimeout) {
        clearTimeout((eventSource as any).messageTimeout);
      }
      if (isCompleted) {
        // 服务端正常关闭后浏览器会触发error并尝试重连，这里忽略
        console.log('[api] SSE error after completed (ignored)');
        return;
      }
      console.warn('[api] SSE error event:', event);
      onError(new Error('SSE连接错误'));

      // 只有在非首次连接错误时才显示错误
      if (eventSource.readyState === EventSource.CONNECTING) {
        if (!isCompleted) console.log('[api] SSE reconnecting...');
      } else if (eventSource.readyState === EventSource.CLOSED) {
        console.log('[api] SSE closed');
      }
    };

    return eventSource;
  }
}

export const apiService = new ApiService();
